/**
 * Confirm_Modal — the review-and-submit surface presented after a successful
 * `POST /api/parse`.
 *
 * Flow:
 *   1. `open(extract)` is invoked with a `DeckExtract` — the modal opens
 *      within 500 ms of the parse response (Requirement 6.1), every field
 *      is prefilled from the extract, and `warnings` are surfaced in a
 *      banner (Requirement 6.2).
 *   2. `legacySignals` render as clickable chips. Clicking a chip whose
 *      `numericValue` is set overwrites the mapped numeric field
 *      (Requirement 5.6); chips without `numericValue` show a non-blocking
 *      hint (Requirement 5.7).
 *   3. On submit, the modal mirrors server-side validation (Requirement 9
 *      via task 6.2). Invalid state blocks the dispatch, focuses the first
 *      invalid field (Requirement 6.4), and never issues a request.
 *   4. Valid submissions issue exactly one `apiClient.createCase(draft)`
 *      call (Requirement 6.5). On success the modal closes and fires
 *      `onCaseCreated(case)` (Requirement 6.9); on error the state is
 *      preserved and the envelope is surfaced (Requirement 6.8).
 *   5. Cancel closes without dispatching and clears local state
 *      (Requirement 6.6).
 *
 * The DOM approach is a `<dialog>` element with a `.modal-backdrop`
 * sibling. `HTMLDialogElement.showModal()` is used when available so the
 * browser handles focus trap and Escape-to-close; otherwise the modal
 * falls back to a plain `.open` attribute — jsdom does not implement
 * `showModal()` but the test suite exercises the same DOM tree.
 *
 * The view has no store subscription — the shell (task 7.7) wires
 * `apiClient` and the `onCaseCreated` callback here, and drives `open()`
 * from its upload handler.
 */
import type {
  ApiErrorEnvelope,
  Case,
  CaseDraft,
  CaseStatus,
  DeckExtract,
  LegacySignal,
  Move,
  ParseWarning,
  StateSnapshot,
} from "@aim/types";
import { ApiError, type ApiClient } from "../clients/apiClient";

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/** Controller returned by `mount()`. */
export interface ModalController {
  /**
   * Open the modal, prefilling every field from `extract`.
   * Idempotent — re-opening while already open replaces the current
   * extract and resets local state.
   *
   * @param extract  The parse response body.
   * @param context  Optional context supplied by the caller (upload handler).
   *   `deckFileName` is the original uploaded filename; when omitted a
   *   fallback is synthesized from the extract's title.
   */
  open(extract: DeckExtract, context?: { deckFileName?: string }): void;

  /** Close the modal and discard local state. */
  close(): void;

  /** Whether the modal is currently open. */
  isOpen(): boolean;

  /** Unmount and release cached element references. */
  destroy(): void;
}

/** Options for `mount()`. */
export interface ModalMountOptions {
  /** Injected API client — used for the single-shot `POST /api/cases`. */
  apiClient: ApiClient;
  /** Fires once with the persisted `Case` after a successful submit. */
  onCaseCreated?: (persisted: Case) => void;
  /** Fires when the user cancels or the modal is programmatically closed. */
  onCancel?: () => void;
}

/**
 * Server-mirroring validation error codes surfaced by the client.
 * See Requirement 9 and task 6.2 for the canonical vocabulary.
 */
export type ClientValidationCode =
  | "INVALID_TITLE"
  | "INVALID_STATUS"
  | "INVALID_DATE"
  | "INVALID_NUMERIC_FIELD"
  | "INVALID_TTV";

/** Fields the modal binds to inputs; used as keys in the error map. */
export type EditableField =
  | "title"
  | "businessUnit"
  | "owner"
  | "dateISO"
  | "status"
  | "revenue"
  | "productivity"
  | "adoption"
  | "tasks"
  | "ttv"
  | "notes";

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Numeric fields validated as non-negative finite. Requirement 9.4. */
const NUMERIC_FIELDS = ["revenue", "productivity", "adoption", "tasks"] as const;
type NumericField = (typeof NUMERIC_FIELDS)[number];

/** Status values accepted by the server. Requirement 9.2 / 6.7. */
const STATUS_VALUES: readonly CaseStatus[] = [
  "Pipeline",
  "Development",
  "Production",
];

/** Ordered iteration through the fields for "first invalid field" focus. */
const FIELD_ORDER: readonly EditableField[] = [
  "title",
  "businessUnit",
  "owner",
  "dateISO",
  "status",
  "revenue",
  "productivity",
  "adoption",
  "tasks",
  "ttv",
  "notes",
];

/**
 * Raw values captured from the form controls. Kept intentionally string-y
 * so validation can distinguish "empty" from "zero" and NaN.
 */
interface FormValues {
  title: string;
  businessUnit: string;
  owner: string;
  dateISO: string;
  status: string;
  revenue: string;
  productivity: string;
  adoption: string;
  tasks: string;
  ttv: string;
  notes: string;
}

/**
 * Well-formed subset of a `CaseDraft` produced from valid form values.
 * The modal composes this with the deck-side fields (baseline/target/moves/
 * warnings/deckSha256/deckFileName) before dispatching.
 */
interface DraftCore {
  title: string;
  businessUnit: string | null;
  owner: string | null;
  dateISO: string; // ISO 8601 UTC — normalized from a date-only input.
  folder: string;
  status: CaseStatus;
  revenue: number;
  productivity: number;
  adoption: number;
  tasks: number;
  ttv: number | null;
  notes: string | null;
}

type ValidationResult =
  | { ok: true; core: DraftCore }
  | { ok: false; errors: Map<EditableField, ClientValidationCode> };

/** Parse a non-empty numeric string into a non-negative finite number. */
function parseNonNegFinite(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Trim, treat empty string as `null`. */
function nullableString(raw: string): string | null {
  const t = raw.trim();
  return t.length === 0 ? null : t;
}

/**
 * Derive the en-US "Month YYYY" folder string from an ISO date.
 * Requirement 9.7. This mirrors what the server does at persistence time
 * so the round-trip stays lossless.
 */
export function deriveFolder(dateISO: string): string {
  const t = Date.parse(dateISO);
  if (!Number.isFinite(t)) return "Unfiled";
  const d = new Date(t);
  return d.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Convert whatever the `<input type="date">` control emits (a `YYYY-MM-DD`
 * string in browsers, or in jsdom an unrestricted string) into a canonical
 * ISO 8601 UTC timestamp.
 *
 * Returns `null` when the input cannot be parsed as a finite date.
 */
function toIsoUtc(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  // date-only strings ("YYYY-MM-DD") are parsed as UTC midnight by the
  // spec; anything else we accept as-is so long as Date.parse agrees.
  const t = Date.parse(trimmed);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toISOString();
}

/**
 * Validate form values against the server-side rules (Requirement 9).
 *
 * Returns either a well-formed `DraftCore` or a map of the first error
 * per field, in insertion order matching `FIELD_ORDER`.
 */
export function validateFormValues(values: FormValues): ValidationResult {
  const errors = new Map<EditableField, ClientValidationCode>();
  const title = values.title.trim();
  if (title.length < 1 || title.length > 200) {
    errors.set("title", "INVALID_TITLE");
  }

  // Status must be strictly one of the enum values (Requirement 6.7 / 9.2).
  if (!STATUS_VALUES.includes(values.status as CaseStatus)) {
    errors.set("status", "INVALID_STATUS");
  }

  const dateISO = toIsoUtc(values.dateISO);
  if (dateISO === null) {
    errors.set("dateISO", "INVALID_DATE");
  }

  // Non-negative finite numerics (Requirement 9.4).
  const numeric: Partial<Record<NumericField, number>> = {};
  for (const field of NUMERIC_FIELDS) {
    const parsed = parseNonNegFinite(values[field]);
    if (parsed === null) {
      errors.set(field, "INVALID_NUMERIC_FIELD");
    } else {
      numeric[field] = parsed;
    }
  }

  // ttv is optional; only validated when present (Requirement 9.5).
  const ttvRaw = values.ttv.trim();
  let ttv: number | null = null;
  if (ttvRaw.length > 0) {
    const parsed = parseNonNegFinite(ttvRaw);
    if (parsed === null) {
      errors.set("ttv", "INVALID_TTV");
    } else {
      ttv = parsed;
    }
  }

  if (errors.size > 0) {
    return { ok: false, errors };
  }

  // At this point every field has passed validation; the non-null
  // assertions below are backed by the checks above.
  return {
    ok: true,
    core: {
      title,
      businessUnit: nullableString(values.businessUnit),
      owner: nullableString(values.owner),
      dateISO: dateISO as string,
      folder: deriveFolder(dateISO as string),
      status: values.status as CaseStatus,
      revenue: numeric.revenue as number,
      productivity: numeric.productivity as number,
      adoption: numeric.adoption as number,
      tasks: numeric.tasks as number,
      ttv,
      notes: nullableString(values.notes),
    },
  };
}

// ---------------------------------------------------------------------------
// Prefill helpers
// ---------------------------------------------------------------------------

/**
 * Reduce a `DeckExtract`'s ISO timestamp into the `YYYY-MM-DD` string
 * accepted by `<input type="date">`. Falls back to today when the extract
 * carries an unparseable date.
 */
function toDateInputValue(iso: string): string {
  const t = Date.parse(iso);
  const d = Number.isFinite(t) ? new Date(t) : new Date();
  const year = d.getUTCFullYear().toString().padStart(4, "0");
  const month = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Values to initialize the form with when a fresh extract arrives.
 *
 * Numeric aggregate fields (revenue/productivity/adoption/tasks) are not
 * carried on `DeckExtract`; they default to `"0"` so the analyst can
 * submit a plain "just-landed" case without touching every field. TTV
 * stays empty (optional).
 */
function initialFormValues(extract: DeckExtract): FormValues {
  return {
    title: extract.title,
    businessUnit: "",
    owner: "",
    dateISO: toDateInputValue(extract.dateISO),
    // First render defaults to Pipeline; the analyst adjusts before submit.
    status: "Pipeline" satisfies CaseStatus,
    revenue: "0",
    productivity: "0",
    adoption: "0",
    tasks: "0",
    ttv: "",
    notes: "",
  };
}

/**
 * Legacy signal field identifier → editable form field.
 * `text` chips have no numeric target; they always trigger the hint path
 * (Requirement 5.7).
 */
function targetFieldForSignal(
  signal: LegacySignal,
): NumericField | null {
  switch (signal.field) {
    case "revenue":
      return "revenue";
    case "adoption":
      return "adoption";
    case "tasks":
      return "tasks";
    case "text":
      return null;
  }
}

// ---------------------------------------------------------------------------
// DOM construction
// ---------------------------------------------------------------------------

/** Cached references into the DOM tree so re-renders stay cheap. */
interface ModalRefs {
  dialog: HTMLDialogElement;
  backdrop: HTMLElement;
  form: HTMLFormElement;
  warningsBanner: HTMLElement;
  chipContainer: HTMLElement;
  chipHint: HTMLElement;
  serverError: HTMLElement;
  cancelButton: HTMLButtonElement;
  submitButton: HTMLButtonElement;
  inputs: Record<EditableField, HTMLElement>;
}

/**
 * A `<dialog>` shell built once at `mount` time. The children are cleared
 * and repopulated on every `open()` so re-opens do not carry stale text.
 */
function buildShell(container: HTMLElement): ModalRefs {
  container.replaceChildren();

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.hidden = true;

  const dialog = document.createElement("dialog");
  dialog.className = "confirm-modal";

  const form = document.createElement("form");
  form.className = "modal-form";
  form.setAttribute("novalidate", "novalidate");

  const header = document.createElement("header");
  header.className = "modal-header";
  const heading = document.createElement("h2");
  heading.textContent = "Confirm case";
  header.append(heading);

  const warningsBanner = document.createElement("div");
  warningsBanner.className = "warnings-banner";
  warningsBanner.hidden = true;

  const fields = document.createElement("div");
  fields.className = "modal-fields";

  const inputs: Record<EditableField, HTMLElement> = {
    title: makeTextInput(fields, "title", "Title", { required: true, maxLength: 200 }),
    businessUnit: makeTextInput(fields, "businessUnit", "Business unit"),
    owner: makeTextInput(fields, "owner", "Owner"),
    dateISO: makeDateInput(fields, "dateISO", "Date"),
    status: makeStatusSelect(fields),
    revenue: makeNumberInput(fields, "revenue", "Revenue impact (USD)"),
    productivity: makeNumberInput(fields, "productivity", "Productivity (hrs)"),
    adoption: makeNumberInput(fields, "adoption", "Adoption (users)"),
    tasks: makeNumberInput(fields, "tasks", "Tasks run"),
    ttv: makeNumberInput(fields, "ttv", "Time to value (days)"),
    notes: makeTextareaInput(fields, "notes", "Notes"),
  };

  const chipContainer = document.createElement("div");
  chipContainer.className = "legacy-chips";
  chipContainer.hidden = true;

  const chipHint = document.createElement("div");
  chipHint.className = "chip-hint";
  chipHint.setAttribute("role", "status");
  chipHint.setAttribute("aria-live", "polite");
  chipHint.hidden = true;

  const serverError = document.createElement("div");
  serverError.className = "server-error";
  serverError.setAttribute("role", "alert");
  serverError.hidden = true;

  const footer = document.createElement("footer");
  footer.className = "modal-footer";

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "cancel";
  cancelButton.textContent = "Cancel";

  const submitButton = document.createElement("button");
  submitButton.type = "submit";
  submitButton.className = "submit";
  submitButton.textContent = "Add case";

  footer.append(cancelButton, submitButton);

  form.append(
    header,
    warningsBanner,
    fields,
    chipContainer,
    chipHint,
    serverError,
    footer,
  );
  dialog.append(form);
  container.append(backdrop, dialog);

  return {
    dialog,
    backdrop,
    form,
    warningsBanner,
    chipContainer,
    chipHint,
    serverError,
    cancelButton,
    submitButton,
    inputs,
  };
}

/** Attach a labeled `<input type="text">` and return the input element. */
function makeTextInput(
  parent: HTMLElement,
  name: EditableField,
  label: string,
  opts?: { required?: boolean; maxLength?: number },
): HTMLInputElement {
  const wrapper = document.createElement("label");
  wrapper.className = `field field-${name}`;
  const span = document.createElement("span");
  span.className = "field-label";
  span.textContent = label;
  const input = document.createElement("input");
  input.type = "text";
  input.name = name;
  if (opts?.required === true) input.required = true;
  if (opts?.maxLength !== undefined) input.maxLength = opts.maxLength;
  wrapper.append(span, input);
  parent.append(wrapper);
  return input;
}

function makeDateInput(
  parent: HTMLElement,
  name: EditableField,
  label: string,
): HTMLInputElement {
  const wrapper = document.createElement("label");
  wrapper.className = `field field-${name}`;
  const span = document.createElement("span");
  span.className = "field-label";
  span.textContent = label;
  const input = document.createElement("input");
  input.type = "date";
  input.name = name;
  input.required = true;
  wrapper.append(span, input);
  parent.append(wrapper);
  return input;
}

function makeNumberInput(
  parent: HTMLElement,
  name: EditableField,
  label: string,
): HTMLInputElement {
  const wrapper = document.createElement("label");
  wrapper.className = `field field-${name}`;
  const span = document.createElement("span");
  span.className = "field-label";
  span.textContent = label;
  const input = document.createElement("input");
  input.type = "number";
  input.name = name;
  input.min = "0";
  input.step = "any";
  input.inputMode = "decimal";
  wrapper.append(span, input);
  parent.append(wrapper);
  return input;
}

function makeStatusSelect(parent: HTMLElement): HTMLSelectElement {
  const wrapper = document.createElement("label");
  wrapper.className = "field field-status";
  const span = document.createElement("span");
  span.className = "field-label";
  span.textContent = "Status";
  const select = document.createElement("select");
  select.name = "status";
  for (const value of STATUS_VALUES) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = value;
    select.append(opt);
  }
  wrapper.append(span, select);
  parent.append(wrapper);
  return select;
}

function makeTextareaInput(
  parent: HTMLElement,
  name: EditableField,
  label: string,
): HTMLTextAreaElement {
  const wrapper = document.createElement("label");
  wrapper.className = `field field-${name}`;
  const span = document.createElement("span");
  span.className = "field-label";
  span.textContent = label;
  const ta = document.createElement("textarea");
  ta.name = name;
  ta.maxLength = 500;
  wrapper.append(span, ta);
  parent.append(wrapper);
  return ta;
}

// ---------------------------------------------------------------------------
// Rendering helpers
// ---------------------------------------------------------------------------

function renderWarnings(el: HTMLElement, warnings: readonly ParseWarning[]): void {
  if (warnings.length === 0) {
    el.hidden = true;
    el.replaceChildren();
    return;
  }
  el.hidden = false;
  const heading = document.createElement("div");
  heading.className = "warnings-heading";
  heading.textContent =
    warnings.length === 1
      ? "1 parse warning"
      : `${String(warnings.length)} parse warnings`;
  const list = document.createElement("ul");
  list.className = "warnings-list";
  for (const w of warnings) {
    const li = document.createElement("li");
    li.className = "warning-entry";
    const code = document.createElement("code");
    code.textContent = w.code;
    const msg = document.createElement("span");
    msg.textContent = ` — ${w.message}`;
    li.append(code, msg);
    list.append(li);
  }
  el.replaceChildren(heading, list);
}

function renderServerError(
  el: HTMLElement,
  envelope: ApiErrorEnvelope | null,
): void {
  if (envelope === null) {
    el.hidden = true;
    el.replaceChildren();
    return;
  }
  el.hidden = false;
  const code = document.createElement("code");
  code.textContent = envelope.code;
  const msg = document.createElement("span");
  msg.textContent = ` — ${envelope.message}`;
  el.replaceChildren(code, msg);
}

function renderChipHint(el: HTMLElement, message: string | null): void {
  if (message === null) {
    el.hidden = true;
    el.textContent = "";
    return;
  }
  el.hidden = false;
  el.textContent = message;
}

function renderFieldErrors(
  refs: ModalRefs,
  errors: Map<EditableField, ClientValidationCode>,
): void {
  for (const field of FIELD_ORDER) {
    const input = refs.inputs[field];
    const invalid = errors.has(field);
    input.classList.toggle("invalid", invalid);
    if (invalid) {
      input.setAttribute("aria-invalid", "true");
    } else {
      input.removeAttribute("aria-invalid");
    }
  }
}

/** Read every form control into a `FormValues` object. */
function readFormValues(refs: ModalRefs): FormValues {
  const g = (f: EditableField): string => {
    const el = refs.inputs[f] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    return el.value;
  };
  return {
    title: g("title"),
    businessUnit: g("businessUnit"),
    owner: g("owner"),
    dateISO: g("dateISO"),
    status: g("status"),
    revenue: g("revenue"),
    productivity: g("productivity"),
    adoption: g("adoption"),
    tasks: g("tasks"),
    ttv: g("ttv"),
    notes: g("notes"),
  };
}

/** Set every form control from a `FormValues` object. */
function writeFormValues(refs: ModalRefs, values: FormValues): void {
  const s = (f: EditableField, v: string): void => {
    const el = refs.inputs[f] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    el.value = v;
  };
  s("title", values.title);
  s("businessUnit", values.businessUnit);
  s("owner", values.owner);
  s("dateISO", values.dateISO);
  s("status", values.status);
  s("revenue", values.revenue);
  s("productivity", values.productivity);
  s("adoption", values.adoption);
  s("tasks", values.tasks);
  s("ttv", values.ttv);
  s("notes", values.notes);
}

/**
 * Render the legacy-chip row. Each chip is a `<button type="button">`
 * carrying `data-field` and (optionally) `data-value` attributes so the
 * click handler can dispatch to the right form field without recomputing
 * the mapping.
 */
function renderChips(
  el: HTMLElement,
  signals: readonly LegacySignal[],
): void {
  if (signals.length === 0) {
    el.hidden = true;
    el.replaceChildren();
    return;
  }
  el.hidden = false;
  const nodes: HTMLElement[] = [];
  for (let i = 0; i < signals.length; i += 1) {
    const signal = signals[i]!;
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "legacy-chip";
    chip.dataset.field = signal.field;
    chip.dataset.index = String(i);
    if (signal.numericValue !== null) {
      chip.dataset.value = String(signal.numericValue);
    } else {
      chip.dataset.noNumeric = "true";
    }
    const label = document.createElement("span");
    label.className = "chip-label";
    label.textContent = signal.label;
    const value = document.createElement("span");
    value.className = "chip-value";
    value.textContent = signal.displayValue;
    chip.append(label, value);
    nodes.push(chip);
  }
  el.replaceChildren(...nodes);
}

// ---------------------------------------------------------------------------
// Extract → CaseDraft assembly
// ---------------------------------------------------------------------------

/**
 * Assemble the final `CaseDraft` from validated form values plus the
 * deck-side fields carried on the extract. All read-only arrays are
 * cloned via spread to satisfy `Case`'s (non-readonly at the mutable
 * `CaseDraft` boundary) shape.
 */
function assembleDraft(
  core: DraftCore,
  extract: DeckExtract,
  deckFileName: string,
): CaseDraft {
  const baseline: StateSnapshot | null =
    extract.baseline === null ? null : { ...extract.baseline };
  const target: StateSnapshot | null =
    extract.target === null ? null : { ...extract.target };
  const moves: readonly Move[] = extract.moves.map((m) => ({ ...m }));
  const parseWarnings: readonly ParseWarning[] = extract.warnings.map((w) => ({
    ...w,
  }));

  return {
    title: core.title,
    businessUnit: core.businessUnit,
    owner: core.owner,
    dateISO: core.dateISO,
    folder: core.folder,
    status: core.status,
    baseline,
    target,
    moves,
    revenue: core.revenue,
    productivity: core.productivity,
    ttv: core.ttv,
    adoption: core.adoption,
    tasks: core.tasks,
    deckFileName,
    deckSha256: extract.deckSha256,
    parseWarnings,
    notes: core.notes,
  };
}

/**
 * Fallback deck filename derived from the extract when the caller does
 * not supply one. Uses the title (sanitized to a filesystem-safe form)
 * or the SHA-256 prefix when the title is empty.
 */
function fallbackDeckFileName(extract: DeckExtract): string {
  const t = extract.title.trim();
  const base = t.length > 0 ? t.replace(/[^A-Za-z0-9._-]+/g, "_") : `deck-${extract.deckSha256.slice(0, 8)}`;
  return `${base}.pptx`;
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

/**
 * Mount the confirm modal into `container` and return a controller.
 *
 * The controller is stateful: only one modal is expected per shell, and
 * `open()` reuses the same DOM tree across cases. Callers wire up the
 * `apiClient` and the `onCaseCreated` callback here.
 */
export function mount(
  container: HTMLElement,
  opts: ModalMountOptions,
): ModalController {
  const refs = buildShell(container);

  // Per-open mutable state.
  let currentExtract: DeckExtract | null = null;
  let deckFileName = "";
  let submitting = false;

  // Focus the first invalid field after a blocked submit.
  const focusFirstInvalid = (
    errors: Map<EditableField, ClientValidationCode>,
  ): void => {
    for (const field of FIELD_ORDER) {
      if (errors.has(field)) {
        const el = refs.inputs[field] as HTMLElement & { focus?: () => void };
        if (typeof el.focus === "function") el.focus();
        return;
      }
    }
  };

  const openDialog = (): void => {
    refs.backdrop.hidden = false;
    // jsdom does not implement `HTMLDialogElement.showModal`, so we
    // gate on presence. When absent, the `open` attribute is set so
    // CSS + tests still see the modal as visible.
    const dialog = refs.dialog as HTMLDialogElement & {
      showModal?: () => void;
    };
    if (typeof dialog.showModal === "function") {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute("open", "");
      }
    } else {
      dialog.setAttribute("open", "");
    }
  };

  const closeDialog = (): void => {
    refs.backdrop.hidden = true;
    const dialog = refs.dialog as HTMLDialogElement & { close?: () => void };
    if (typeof dialog.close === "function" && dialog.hasAttribute("open")) {
      try {
        dialog.close();
      } catch {
        dialog.removeAttribute("open");
      }
    } else {
      dialog.removeAttribute("open");
    }
  };

  const resetState = (): void => {
    currentExtract = null;
    deckFileName = "";
    submitting = false;
    refs.submitButton.disabled = false;
    renderServerError(refs.serverError, null);
    renderChipHint(refs.chipHint, null);
    renderFieldErrors(refs, new Map());
  };

  // Chip click delegation — one listener on the container, dispatched via
  // dataset. Chips without `numericValue` (marked with `data-noNumeric`)
  // surface a non-blocking hint per Requirement 5.7.
  const onChipClick = (ev: Event): void => {
    const target = ev.target as HTMLElement | null;
    if (target === null) return;
    const chip = target.closest<HTMLElement>(".legacy-chip");
    if (chip === null || currentExtract === null) return;
    ev.preventDefault();

    const idxStr = chip.dataset.index;
    if (idxStr === undefined) return;
    const idx = Number(idxStr);
    const signal = currentExtract.legacySignals[idx];
    if (signal === undefined) return;

    const field = targetFieldForSignal(signal);
    if (field === null || signal.numericValue === null) {
      renderChipHint(
        refs.chipHint,
        `The "${signal.label}" chip has no numeric value to apply.`,
      );
      return;
    }

    // Overwrite the mapped field. Use the parsed number so the field
    // accepts the value even if the display string included symbols.
    const input = refs.inputs[field] as HTMLInputElement;
    input.value = String(signal.numericValue);
    renderChipHint(
      refs.chipHint,
      `Applied "${signal.label}" (${signal.displayValue}) to ${field}.`,
    );
  };

  refs.chipContainer.addEventListener("click", onChipClick);

  // Cancel closes without dispatching (Requirement 6.6).
  const onCancel = (ev: Event): void => {
    ev.preventDefault();
    closeDialog();
    resetState();
    if (opts.onCancel !== undefined) opts.onCancel();
  };
  refs.cancelButton.addEventListener("click", onCancel);

  // Backdrop click also cancels — matches the analyst's mental model.
  refs.backdrop.addEventListener("click", onCancel);

  // Native Escape → cancel on real browsers where `<dialog>` fires `cancel`.
  refs.dialog.addEventListener("cancel", (ev) => {
    ev.preventDefault();
    onCancel(ev);
  });

  // Submit runs client-side validation, then dispatches at most one
  // `createCase` request (Requirement 6.5).
  const onSubmit = (ev: Event): void => {
    ev.preventDefault();
    if (submitting || currentExtract === null) return;

    const values = readFormValues(refs);
    const result = validateFormValues(values);
    if (!result.ok) {
      renderFieldErrors(refs, result.errors);
      focusFirstInvalid(result.errors);
      return;
    }
    renderFieldErrors(refs, new Map());

    // Freeze the extract at submit time so a late reopen cannot swap
    // draft contents mid-flight.
    const extractAtSubmit = currentExtract;
    const nameAtSubmit = deckFileName;
    const draft = assembleDraft(result.core, extractAtSubmit, nameAtSubmit);

    submitting = true;
    refs.submitButton.disabled = true;
    // Clear stale error on retry so the analyst sees fresh feedback.
    renderServerError(refs.serverError, null);

    void opts.apiClient
      .createCase(draft)
      .then((persisted) => {
        // Success: close + fire callback. Requirement 6.9.
        closeDialog();
        resetState();
        if (opts.onCaseCreated !== undefined) opts.onCaseCreated(persisted);
      })
      .catch((cause: unknown) => {
        // Preserve state, surface envelope, re-enable submit.
        // Requirement 6.8.
        const envelope = extractEnvelope(cause);
        renderServerError(refs.serverError, envelope);
        submitting = false;
        refs.submitButton.disabled = false;
      });
  };
  refs.form.addEventListener("submit", onSubmit);

  return {
    open(extract, context): void {
      currentExtract = extract;
      deckFileName =
        context?.deckFileName ?? fallbackDeckFileName(extract);
      submitting = false;

      writeFormValues(refs, initialFormValues(extract));
      renderFieldErrors(refs, new Map());
      renderWarnings(refs.warningsBanner, extract.warnings);
      renderChips(refs.chipContainer, extract.legacySignals);
      renderChipHint(refs.chipHint, null);
      renderServerError(refs.serverError, null);
      refs.submitButton.disabled = false;

      openDialog();

      // Focus the title input on open so keyboard users land in the
      // most likely edit target.
      const titleEl = refs.inputs.title as HTMLInputElement;
      if (typeof titleEl.focus === "function") {
        titleEl.focus();
      }
    },

    close(): void {
      closeDialog();
      resetState();
    },

    isOpen(): boolean {
      return refs.dialog.hasAttribute("open");
    },

    destroy(): void {
      refs.chipContainer.removeEventListener("click", onChipClick);
      refs.cancelButton.removeEventListener("click", onCancel);
      refs.backdrop.removeEventListener("click", onCancel);
      refs.form.removeEventListener("submit", onSubmit);
      refs.backdrop.remove();
      refs.dialog.remove();
      currentExtract = null;
    },
  };
}

/**
 * Extract a canonical `ApiErrorEnvelope` from anything thrown by the API
 * client. `ApiError` carries the parsed envelope on `.envelope`; anything
 * else collapses to a synthesized `NETWORK_ERROR`.
 */
function extractEnvelope(cause: unknown): ApiErrorEnvelope {
  if (cause instanceof ApiError) {
    return cause.envelope;
  }
  if (cause instanceof Error) {
    return { code: "NETWORK_ERROR", message: cause.message };
  }
  return { code: "NETWORK_ERROR", message: "Request failed." };
}

// ---------------------------------------------------------------------------
// Promise-based factory (task 7.7 consumer surface)
// ---------------------------------------------------------------------------

/**
 * Promise-based controller returned by `createModal`.
 *
 * `open` resolves with the persisted `Case` on a successful submit or
 * `null` when the analyst cancels / the modal is closed programmatically.
 * The promise never rejects — server error envelopes are surfaced inside
 * the modal (Requirement 6.8) and the analyst may retry or cancel; the
 * outer promise only settles once the modal reaches a terminal state.
 */
export interface Modal {
  /**
   * Open the modal on `extract` and resolve with the outcome.
   *
   * When called while another open cycle is still pending, the previous
   * cycle resolves with `null` (as if cancelled) and the new one takes
   * over — this matches the semantics of a fresh parse arriving before
   * the analyst finished the last one.
   */
  open(
    extract: DeckExtract,
    context?: { deckFileName?: string },
  ): Promise<Case | null>;

  /** Close the modal; a pending `open` promise resolves with `null`. */
  close(): void;

  /** Unmount and release cached element references. */
  destroy(): void;
}

/**
 * `createModal` — the consumer-facing factory used by the portal shell.
 *
 * Wraps `mount()` in a promise-based facade so callers can write:
 *
 * ```
 * const modal = createModal(root, apiClient);
 * const persisted = await modal.open(extract);
 * if (persisted !== null) store.set({ cases: [...cases, persisted] });
 * ```
 *
 * The wrapper reuses the underlying `ModalController`, so behavior
 * (prefill, warnings banner, legacy chips, validation gating, single-shot
 * dispatch, cancel discard, server-error surfacing) is identical to
 * `mount()`.
 */
export function createModal(root: HTMLElement, apiClient: ApiClient): Modal {
  // A single pending resolver per open cycle. Re-opens or `close()` calls
  // fulfill the current one with `null` before starting the next cycle.
  let pending: ((value: Case | null) => void) | null = null;

  const resolvePending = (value: Case | null): void => {
    if (pending === null) return;
    const r = pending;
    pending = null;
    r(value);
  };

  const controller = mount(root, {
    apiClient,
    onCaseCreated: (persisted) => {
      resolvePending(persisted);
    },
    onCancel: () => {
      resolvePending(null);
    },
  });

  return {
    open(extract, context): Promise<Case | null> {
      // Any prior in-flight open cycle is treated as cancelled from the
      // caller's perspective — the new parse supersedes it.
      resolvePending(null);
      return new Promise<Case | null>((resolve) => {
        pending = resolve;
        controller.open(extract, context);
      });
    },
    close(): void {
      controller.close();
      resolvePending(null);
    },
    destroy(): void {
      resolvePending(null);
      controller.destroy();
    },
  };
}
